import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { fixPhotoUrl } from '@/lib/photoUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Calendar,
  Wifi,
  Home,
  Download,
  Upload,
  Plus,
  Users,
  Pencil,
  Trash2,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [rates, setRates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [showIcalDialog, setShowIcalDialog] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [rateForm, setRateForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    nightly_rate: 0,
    monthly_rate: 0,
    rate_14_21_days: 0
  });
  // Inline editing state for LOS tiers
  const [editingRate, setEditingRate]   = useState(null);   // rate id being edited
  const [editingTiers, setEditingTiers] = useState([]);     // working copy of tiers
  const [savingRate, setSavingRate]     = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [propRes, ratesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`),
        axios.get(`${API}/properties/${id}/rates`),
        axios.get(`${API}/bookings?property_id=${id}&limit=10`)
      ]);
      setProperty(propRes.data);
      setRates(ratesRes.data || []);
      setBookings(bookingsRes.data.items || []);
    } catch (error) {
      toast.error('Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/properties/${id}/rates`, rateForm);
      toast.success('Rate added successfully');
      setShowRateDialog(false);
      setRateForm({ name: '', start_date: '', end_date: '', nightly_rate: 0, monthly_rate: 0, rate_14_21_days: 0 });
      fetchData();
    } catch (error) {
      toast.error('Failed to add rate');
    }
  };

  const startEditRate = (rate) => {
    setEditingRate(rate.id);
    setEditingTiers(rate.los_tiers?.length
      ? rate.los_tiers.map(t => ({ ...t }))
      : [{ min_nights: 1, nightly_rate: rate.nightly_rate || 0 }]
    );
  };

  const cancelEditRate = () => { setEditingRate(null); setEditingTiers([]); };

  const updateTier = (idx, field, value) => {
    setEditingTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const addTier = () => {
    const lastNights = editingTiers.length ? editingTiers[editingTiers.length - 1].min_nights : 0;
    setEditingTiers(prev => [...prev, { min_nights: lastNights + 7, nightly_rate: 0 }]);
  };

  const removeTier = (idx) => {
    setEditingTiers(prev => prev.filter((_, i) => i !== idx));
  };

  const saveRateTiers = async (rate) => {
    setSavingRate(true);
    try {
      const sorted = [...editingTiers].sort((a, b) => a.min_nights - b.min_nights);
      const baseRate = sorted[0]?.nightly_rate || rate.nightly_rate;
      await axios.put(`${API}/properties/${id}/rates/${rate.id}`, {
        ...rate,
        los_tiers: sorted,
        nightly_rate: baseRate,
      });
      toast.success('Pricing updated');
      setEditingRate(null);
      setEditingTiers([]);
      fetchData();
    } catch {
      toast.error('Failed to save pricing');
    } finally {
      setSavingRate(false);
    }
  };

  const deleteRate = async (rateId) => {
    if (!window.confirm('Remove this rate?')) return;
    try {
      await axios.delete(`${API}/properties/${id}/rates/${rateId}`);
      toast.success('Rate removed');
      fetchData();
    } catch {
      toast.error('Failed to remove rate');
    }
  };

  const handleIcalImport = async () => {
    if (!icalUrl) return;
    try {
      const response = await axios.post(`${API}/ical/${id}/import?ical_url=${encodeURIComponent(icalUrl)}`);
      toast.success(`Imported ${response.data.count} events`);
      setShowIcalDialog(false);
      setIcalUrl('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import calendar');
    }
  };

  const getAmenityIcon = (amenity) => {
    const icons = {
      'WiFi': Wifi,
      'Pool': Home,
      'Kitchen': Home,
    };
    const Icon = icons[amenity] || Home;
    return <Icon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold text-[#0c2431]">Property not found</h2>
        <Link to="/properties" className="text-[#194a50] mt-4 inline-block">
          ← Back to properties
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/properties">
          <Button variant="ghost" size="icon" className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">{property.title}</h1>
          <div className="flex items-center gap-2 text-[#194a50]/70 mt-1">
            <MapPin className="w-4 h-4" />
            {property.location?.address}, {property.location?.city}, {property.location?.country}
          </div>
        </div>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/ical/${id}/export`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="border-[#194a50]/20" data-testid="export-ical-btn">
              <Download className="w-4 h-4 mr-2" />
              Export iCal
            </Button>
          </a>
          {isAdmin && (
            <Dialog open={showIcalDialog} onOpenChange={setShowIcalDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#194a50]/20" data-testid="import-ical-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import iCal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import External Calendar</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>iCal URL</Label>
                    <Input
                      value={icalUrl}
                      onChange={(e) => setIcalUrl(e.target.value)}
                      placeholder="https://example.com/calendar.ics"
                      data-testid="ical-url-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowIcalDialog(false)}>Cancel</Button>
                    <Button onClick={handleIcalImport} className="bg-[#194a50] hover:bg-[#10555e]" data-testid="import-confirm-btn">
                      Import
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Photo Gallery */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {property.photos?.slice(0, 3).map((photo, idx) => (
          <div key={idx} className={`relative rounded-xl overflow-hidden ${idx === 0 ? 'md:col-span-2 md:row-span-2 h-80' : 'h-36'}`}>
            <img src={fixPhotoUrl(photo)} alt={`${property.title} ${idx + 1}`} className="w-full h-full object-cover" />
          </div>
        ))}
        {(!property.photos || property.photos.length === 0) && (
          <div className="md:col-span-3 h-64 bg-[#e1dad2] rounded-xl flex items-center justify-center">
            <Home className="w-20 h-20 text-[#194a50]/20" />
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList className="bg-[#e1dad2]">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Pricing & Rates</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#194a50]/80 leading-relaxed">{property.description}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#194a50]/70">Base Rate</span>
                  <span className="font-semibold text-[#0c2431]">${property.base_nightly_rate}/night</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#194a50]/70">Min Stay</span>
                  <span className="font-semibold text-[#0c2431]">{property.min_stay_nights} nights</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#194a50]/70">Cleaning Fee</span>
                  <span className="font-semibold text-[#0c2431]">${property.cleaning_fee}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#194a50]/70">Check-in Fee</span>
                  <span className="font-semibold text-[#0c2431]">${property.check_in_fee}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Amenities */}
          <Card className="shadow-sm border-[#194a50]/5">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Amenities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {property.amenities?.map((amenity) => (
                  <Badge key={amenity} variant="secondary" className="bg-[#e1dad2] text-[#0c2431] px-4 py-2">
                    {amenity}
                  </Badge>
                ))}
                {(!property.amenities || property.amenities.length === 0) && (
                  <p className="text-[#194a50]/60">No amenities listed</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Operational Notes (Admin only) */}
          {(isAdmin || user?.role === 'OWNER') && property.operational_notes && (
            <Card className="shadow-sm border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="font-['Outfit'] text-amber-800">Operational Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-900">{property.operational_notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rates" className="space-y-4">

          {/* Header + Add button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-[#0c2431] font-['Outfit']">Length-of-Stay Pricing</h3>
              <p className="text-xs text-gray-400 mt-0.5">Price per night decreases as stay length increases</p>
            </div>
            {isAdmin && (
              <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#194a50] hover:bg-[#10555e] gap-1.5">
                    <Plus className="w-4 h-4" /> Add Season
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Seasonal Rate</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddRate} className="space-y-4 mt-4">
                    <div>
                      <Label>Season Name</Label>
                      <Input value={rateForm.name} onChange={(e) => setRateForm({ ...rateForm, name: e.target.value })} placeholder="e.g., Summer Season" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Start Date</Label><Input type="date" value={rateForm.start_date} onChange={(e) => setRateForm({ ...rateForm, start_date: e.target.value })} required /></div>
                      <div><Label>End Date</Label><Input type="date" value={rateForm.end_date} onChange={(e) => setRateForm({ ...rateForm, end_date: e.target.value })} required /></div>
                    </div>
                    <div><Label>Base Nightly Rate (€)</Label><Input type="number" step="0.01" value={rateForm.nightly_rate} onChange={(e) => setRateForm({ ...rateForm, nightly_rate: parseFloat(e.target.value) || 0 })} required /></div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowRateDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]">Add Rate</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {rates.length === 0 ? (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardContent className="py-12 text-center text-[#194a50]/50">No pricing configured yet</CardContent>
            </Card>
          ) : (
            rates.map((rate) => {
              const isEditing = editingRate === rate.id;
              const tiers = isEditing ? editingTiers : (rate.los_tiers || [{ min_nights: 1, nightly_rate: rate.nightly_rate }]);

              return (
                <Card key={rate.id} className="shadow-sm border-[#194a50]/5 overflow-hidden">
                  {/* Season header */}
                  <CardHeader className="pb-2 flex flex-row items-center justify-between bg-[#f8fafa]">
                    <div>
                      <CardTitle className="text-sm font-semibold text-[#194a50] font-['Outfit']">{rate.name}</CardTitle>
                      {rate.start_date && rate.end_date && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {rate.start_date} → {rate.end_date}
                          {rate.repeat_period === 'year' && ' · repeats yearly'}
                        </p>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        {isEditing ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={cancelEditRate} className="gap-1 text-gray-500 h-7 px-2"><X className="w-3.5 h-3.5" /> Cancel</Button>
                            <Button size="sm" onClick={() => saveRateTiers(rate)} disabled={savingRate} className="bg-[#194a50] hover:bg-[#10555e] h-7 px-2 gap-1">
                              <Check className="w-3.5 h-3.5" /> {savingRate ? 'Saving…' : 'Save'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => startEditRate(rate)} className="h-7 px-2 text-gray-400 hover:text-[#194a50]"><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteRate(rate.id)} className="h-7 px-2 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                      </div>
                    )}
                  </CardHeader>

                  <CardContent className="pt-0 pb-4">
                    {/* Tier grid */}
                    <div className="overflow-x-auto mt-3">
                      <table className="w-full text-sm border-separate border-spacing-0">
                        <thead>
                          <tr>
                            {tiers.map((tier, idx) => (
                              <th key={idx} className="text-center pb-1 pr-2 font-medium text-[#194a50]/70 text-xs whitespace-nowrap min-w-[80px]">
                                {idx === 0 ? '1 night' : `${tier.min_nights}+ nights`}
                              </th>
                            ))}
                            {isEditing && <th className="w-8" />}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Min-nights row (edit mode only) */}
                          {isEditing && (
                            <tr>
                              {tiers.map((tier, idx) => (
                                <td key={idx} className="pr-2 pb-1 align-top">
                                  {idx === 0 ? (
                                    <div className="h-8 flex items-center justify-center text-xs text-gray-400 bg-gray-50 rounded border">1</div>
                                  ) : (
                                    <Input
                                      type="number" min="1"
                                      value={tier.min_nights}
                                      onChange={(e) => updateTier(idx, 'min_nights', parseInt(e.target.value) || 1)}
                                      className="h-8 text-xs text-center"
                                    />
                                  )}
                                </td>
                              ))}
                              <td />
                            </tr>
                          )}
                          {/* Price row */}
                          <tr>
                            {tiers.map((tier, idx) => (
                              <td key={idx} className="pr-2 pb-1 align-top">
                                {isEditing ? (
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={tier.nightly_rate}
                                    onChange={(e) => updateTier(idx, 'nightly_rate', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-xs text-center"
                                  />
                                ) : (
                                  <div className="h-8 flex items-center justify-center bg-gray-50 rounded border border-gray-100 font-medium text-[#0c2431] text-xs">
                                    €{Number(tier.nightly_rate).toFixed(2)}
                                  </div>
                                )}
                              </td>
                            ))}
                            {isEditing && (
                              <td className="align-top">
                                <button onClick={removeTier.bind(null, tiers.length - 1)} className="h-8 w-8 flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors" title="Remove last tier">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>

                      {isEditing && (
                        <button onClick={addTier} className="mt-2 text-xs text-[#194a50] hover:text-[#10555e] font-medium flex items-center gap-1 px-1">
                          <Plus className="w-3 h-3" /> Add tier
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="bookings" className="space-y-6">
          <Card className="shadow-sm border-[#194a50]/5">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Recent Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <p className="text-[#194a50]/60 text-center py-8">No bookings for this property</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Dates</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td className="font-medium text-[#0c2431]">{booking.reference}</td>
                          <td className="text-[#194a50]/70">
                            {new Date(booking.start_date).toLocaleDateString()} - {new Date(booking.end_date).toLocaleDateString()}
                          </td>
                          <td>
                            <span className={`badge-${booking.status === 'CONFIRMED' ? 'success' : booking.status === 'PENDING' ? 'warning' : 'error'}`}>
                              {booking.status}
                            </span>
                          </td>
                          <td>
                            <span className={`badge-${booking.payment_status?.includes('PAID') ? 'success' : 'warning'}`}>
                              {booking.payment_status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            <Link to={`/bookings/${booking.id}`} className="text-[#194a50] hover:text-[#10555e]">
                              View →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PropertyDetailPage;
